/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { createSelector } from 'reselect';

import {
  CLIENT,
  INFLUENCER,
  PHOTOGRAPHER,
  MODEL,
  HEAD_STYLIST,
  CLOTHING_STYLIST,
} from 'actions/signup';

const CLIENT_INPUTS = [
  'title',
  'firstname',
  'lastname',
  'email',
  'location',
  'phone_number',
  'wants_newsletter',
  'wants_sms_notifications',
];

const INFLUENCER_INPUTS = [
  'firstname',
  'lastname',
  'phone_number',
  'email',
  'language',
  'talent_presentation',
  'location',

  'talent_website_url',
  'wants_newsletter',
  'wants_sms_notifications',
  'wants_promotion',
  'wants_incognito',
];

const PHOTOGRAPHER_INPUTS = [
  'firstname',
  'lastname',
  'phone_number',
  'email',
  'language',
  'talent_presentation',
  'location',

  'talent_website_url',
  'wants_newsletter',
  'wants_sms_notifications',
  'wants_promotion',
  'wants_incognito',
];

const MODEL_INPUTS = [
  'firstname',
  'lastname',
  'phone_number',
  'email',
  'language',
  'id_picture_cloudinary_public_id',
  'talent_presentation',
  'location',

  'talent_website_url',
  'talent_portfolio',
  'talent_polaroids',
  'model_eye_color',
  'model_hair_color',
  'model_shoe_sizes',
  'model_clothing_sizes',
  'model_height',
  'model_chest',
  'model_waist',
  'model_hips',
  'model_has_tattoos_piercings',
  'wants_newsletter',
  'wants_sms_notifications',
  'wants_promotion',
  'wants_incognito',
];

const HEAD_STYLIST_INPUTS = [
  'firstname',
  'lastname',
  'phone_number',
  'email',
  'language',
  'talent_presentation',
  'location',

  'talent_website_url',
  'stylist_does_face',
  'stylist_does_hair',
  'wants_newsletter',
  'wants_sms_notifications',
  'wants_promotion',
  'wants_incognito',
];

const CLOTHING_STYLIST_INPUTS = [
  'firstname',
  'lastname',
  'phone_number',
  'email',
  'language',
  'talent_presentation',
  'location',

  'talent_website_url',
  'wants_newsletter',
  'wants_sms_notifications',
  'wants_promotion',
  'wants_incognito',
];

const getUser = state => state.user;
const getUserType = state => state.signup.type;
const getProfileForm = state => state.profileForm;
const getConstants = state => state.bootstrap.constants;
const getLanguages = state => state.bootstrap.languages;

export const makeUpdateProfileForm = () =>
  createSelector(
    [getUser, getUserType, getProfileForm, getConstants, getLanguages],
    (user, userType, profileForm, constants, languages) => {
      const fieldsets = profileForm.fieldsets.map(({ legend, inputs }) => ({
        legend,
        inputs: inputs
          .map(input =>
            input.name === 'language'
              ? { ...input, options: languages }
              : { ...input }
          )
          // Passport / ID picture
          .map(input => {
            if (input.name === 'id_picture_cloudinary_public_id') {
              const { id_picture } = user;

              return {
                ...input,
                preview: id_picture ? id_picture.thumbnail_url : '',
                value: id_picture ? id_picture.cloudinary_id : '',
              };
            }

            return { ...input };
          })
          // Fix phone number field.
          .map(input =>
            ['phone_number'].indexOf(input.name) !== -1
              ? { ...input, resendPhoneLink: true }
              : input
          )
          // Disabled fields.
          .map(input =>
            ['lastname', 'firstname', 'phone_number'].indexOf(input.name) !== -1
              ? { ...input, disabled: true }
              : input
          )

          // Disable icognito field if user.admin_incognito_lock
          .map(input =>
            ['wants_incognito'].indexOf(input.name) !== -1 &&
            user.admin_incognito_lock &&
            user.is_admin == false
              ? { ...input, disabled: true }
              : input
          )
          // Regular fields.
          .map(input =>
            typeof user[input.name] !== 'undefined'
              ? { ...input, value: user[input.name] }
              : input
          ),
      }));

      switch (userType) {
        case CLIENT:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs.filter(
                input => CLIENT_INPUTS.indexOf(input.name) !== -1
              ),
            })),
          };

        case INFLUENCER:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => INFLUENCER_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case PHOTOGRAPHER:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => PHOTOGRAPHER_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case MODEL:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => MODEL_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                )
                .map(input =>
                  input.name === 'model_eye_color'
                    ? { ...input, options: constants.eye_colors }
                    : { ...input }
                )
                .map(input =>
                  input.name === 'model_hair_color'
                    ? { ...input, options: constants.hair_colors }
                    : { ...input }
                )
                .map(input =>
                  input.name === 'model_clothing_sizes'
                    ? { ...input, options: constants.clothing_sizes }
                    : { ...input }
                )
                .map(input =>
                  input.name === 'model_shoe_sizes'
                    ? { ...input, options: constants.shoe_sizes }
                    : { ...input }
                ),
            })),
          };

        case HEAD_STYLIST:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(input => HEAD_STYLIST_INPUTS.indexOf(input.name) !== -1)
                .map(input =>
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        case CLOTHING_STYLIST:
          return {
            ...profileForm,
            fieldsets: fieldsets.map(({ legend, inputs }) => ({
              legend,
              inputs: inputs
                .filter(
                  input => CLOTHING_STYLIST_INPUTS.indexOf(input.name) !== -1
                )
                .map(input =>
                  typeof user.roles[user.type][input.name] !== 'undefined'
                    ? { ...input, value: user.roles[user.type][input.name] }
                    : input
                ),
            })),
          };

        default:
          return {};
      }
    }
  );
